import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Menu,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { useBookingStore } from './store/bookingStore';
import { Table as TableType, TableCategory } from './types';
import { ChevronDown, ChevronUp, Plus, Users, MoreVertical, Edit, Trash2, AlertCircle } from 'lucide-react';
import { seedService } from './services/firestore/seed';

const defaultTable = {
  id: '',
  number: 0,
  category: 'indoor',
  capacity: 2,
};

const AdminDashboard: React.FC = () => {
  const [timeFilter, setTimeFilter] = useState('today');
  const [isAddTableModalOpen, setIsAddTableModalOpen] = useState(false);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [isEditTableModalOpen, setIsEditTableModalOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [_isSeeding, setIsSeeding] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);
  const { bookings, tables, updateBookingStatus, addTable, categories, addCategory, updateTable, removeTable, loading, error, fetchData } = useBookingStore();
  const [newTable, setNewTable] = useState<Partial<TableType>>({
    number: 0,
    category: 'indoor',
    capacity: 2,
  });
  const [editingTable, setEditingTable] = useState<TableType>(defaultTable);
  const [newCategory, setNewCategory] = useState('');
  const [tableMenuAnchor, setTableMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const _handleSeedData = async () => {
    setIsSeeding(true);
    setSeedError(null);
    try {
      await seedService.seedDefaultData();
      await fetchData();
    } catch (error) {
      setSeedError('Failed to seed default data');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleAddTable = () => {
    if (newTable.number && newTable.category && newTable.capacity) {
      addTable({
        number: newTable.number,
        category: newTable.category as TableCategory,
        capacity: newTable.capacity,
      });
      setIsAddTableModalOpen(false);
      setNewTable({ number: 0, category: 'indoor', capacity: 2 });
    }
  };

  const handleEditTable = () => {
    updateTable(editingTable.id, {
      number: editingTable.number,
      category: editingTable.category,
      capacity: editingTable.capacity,
    });
    setIsEditTableModalOpen(false);
    setEditingTable(defaultTable);
  };

  const handleAddCategory = () => {
    if (newCategory.trim()) {
      addCategory(newCategory.trim());
      setIsAddCategoryModalOpen(false);
      setNewCategory('');
    }
  };

  const handleTableMenuOpen = (event: React.MouseEvent<HTMLElement>, tableId: string) => {
    event.stopPropagation();
    setTableMenuAnchor(event.currentTarget);
    setSelectedTableId(tableId);
  };

  const handleTableMenuClose = () => {
    setTableMenuAnchor(null);
    setSelectedTableId(null);
  };

  const handleEditTableClick = (table: TableType) => {
    setEditingTable(table);
    setIsEditTableModalOpen(true);
    handleTableMenuClose();
  };

  const handleRemoveTableClick = (tableId: string) => {
    removeTable(tableId);
    handleTableMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditTableModalOpen(false);
    setEditingTable(defaultTable);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    const bookingDate = new Date(booking.date);
    const today = new Date();
    
    switch (timeFilter) {
      case 'today':
        return bookingDate.toDateString() === today.toDateString();
      case 'weekly':
        const weekAgo = new Date(today.setDate(today.getDate() - 7));
        return bookingDate >= weekAgo;
      case 'monthly':
        const monthAgo = new Date(today.setMonth(today.getMonth() - 1));
        return bookingDate >= monthAgo;
      case 'yearly':
        const yearAgo = new Date(today.setFullYear(today.getFullYear() - 1));
        return bookingDate >= yearAgo;
      default:
        return true;
    }
  });

  const groupedTables = tables.reduce((acc, table) => {
    if (!acc[table.category]) {
      acc[table.category] = [];
    }
    acc[table.category].push(table);
    return acc;
  }, {} as Record<string, TableType[]>);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        {/* <Typography variant="h4">
          Admin Dashboard
        </Typography> */}
        {/* <Button
          variant="outlined"
          startIcon={<Database size={18} />}
          onClick={handleSeedData}
          disabled={isSeeding}
        >
          {isSeeding ? 'Seeding...' : 'Seed Default Data'}
        </Button> */}
      </Box>

      <Tabs
        value={timeFilter}
        onChange={(_, newValue) => setTimeFilter(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label="Today" value="today" />
        <Tab label="Weekly" value="weekly" />
        <Tab label="Monthly" value="monthly" />
        <Tab label="Yearly" value="yearly" />
      </Tabs>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Bookings
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Table</TableCell>
                      <TableCell>Guests</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBookings.map((booking) => {
                      const table = tables.find((t) => t.id === booking.tableId);
                      return (
                        <TableRow
                          key={booking.id}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                            bgcolor: booking.status === 'pending' ? 'action.hover' : 'inherit',
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2">{booking.customerName}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {booking.customerPhone}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2">{booking.date}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {booking.timeSlots.join(', ')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {table ? `Table ${table.number} (${table.category})` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Users size={16} />
                              {booking.numberOfGuests}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={booking.status}
                              color={getStatusColor(booking.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {booking.status === 'pending' && (
                              <Box>
                                <Button
                                  onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                  color="success"
                                  size="small"
                                  sx={{ mr: 1 }}
                                >
                                  Accept
                                </Button>
                                <Button
                                  onClick={() => updateBookingStatus(booking.id, 'rejected')}
                                  color="error"
                                  size="small"
                                >
                                  Reject
                                </Button>
                              </Box>
                            )}
                            {booking.status === 'confirmed' && (
                              <Button
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                color="error"
                                size="small"
                              >
                                Cancel
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Tables</Typography>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => setIsAddCategoryModalOpen(true)}
                    startIcon={<Plus size={16} />}
                    sx={{ mr: 1 }}
                  >
                    Add Category
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setIsAddTableModalOpen(true)}
                    startIcon={<Plus size={16} />}
                  >
                    Add Table
                  </Button>
                </Box>
              </Box>
              
              {Object.entries(groupedTables).map(([category, categoryTables]) => (
                <Paper
                  key={category}
                  sx={{ mb: 2, overflow: 'hidden' }}
                  elevation={2}
                >
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                  >
                    <Typography variant="subtitle1">
                      {category.charAt(0).toUpperCase() + category.slice(1)} Tables
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ color: 'inherit' }}
                    >
                      {expandedCategory === category ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </IconButton>
                  </Box>
                  <Collapse in={expandedCategory === category}>
                    <List dense>
                      {categoryTables.map((table) => (
                        <ListItem
                          key={table.id}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                          }}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={(e) => handleTableMenuOpen(e, table.id)}
                            >
                              <MoreVertical size={18} />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">
                                  Table {table.number}
                                </Typography>
                                <Chip
                                  icon={<Users size={14} />}
                                  label={`${table.capacity} seats`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Paper>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Table Modal */}
      <Dialog
        open={isAddTableModalOpen}
        onClose={() => setIsAddTableModalOpen(false)}
      >
        <DialogTitle>Add New Table</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Table Number"
              type="number"
              value={newTable.number}
              onChange={(e) =>
                setNewTable({ ...newTable, number: Number(e.target.value) })
              }
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={newTable.category}
                label="Category"
                onChange={(e) =>
                  setNewTable({
                    ...newTable,
                    category: e.target.value as TableCategory,
                  })
                }
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category?.charAt(0)?.toUpperCase() + category?.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Capacity"
              type="number"
              value={newTable.capacity}
              onChange={(e) =>
                setNewTable({ ...newTable, capacity: Number(e.target.value) })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddTableModalOpen(false)}>Cancel</Button>
          <Button onClick={handleAddTable} variant="contained">
            Add Table
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Table Modal */}
      <Dialog
        open={isEditTableModalOpen}
        onClose={handleCloseEditModal}
      >
        <DialogTitle>Edit Table</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Table Number"
              type="number"
              value={editingTable.number}
              onChange={(e) =>
                setEditingTable({
                  ...editingTable,
                  number: Number(e.target.value)
                })
              }
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={editingTable.category}
                label="Category"
                onChange={(e) =>
                  setEditingTable({
                    ...editingTable,
                    category: e.target.value as TableCategory,
                  })
                }
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category?.charAt(0)?.toUpperCase() + category?.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Capacity"
              type="number"
              value={editingTable.capacity}
              onChange={(e) =>
                setEditingTable({
                  ...editingTable,
                  capacity: Number(e.target.value)
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal}>Cancel</Button>
          <Button onClick={handleEditTable} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Category Modal */}
      <Dialog
        open={isAddCategoryModalOpen}
        onClose={() => setIsAddCategoryModalOpen(false)}
      >
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              helperText="Enter a new category name (e.g., terrace, private)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddCategoryModalOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCategory} variant="contained">
            Add Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table Actions Menu */}
      <Menu
        anchorEl={tableMenuAnchor}
        open={Boolean(tableMenuAnchor)}
        onClose={handleTableMenuClose}
      >
        <MenuItem
          onClick={() => {
            const table = tables.find(t => t.id === selectedTableId);
            if (table) {
              handleEditTableClick(table);
            }
          }}
        >
          <Edit size={18} style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedTableId) {
              handleRemoveTableClick(selectedTableId);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <Trash2 size={18} style={{ marginRight: 8 }} />
          Remove
        </MenuItem>
      </Menu>

      <Snackbar 
        open={Boolean(error || seedError)} 
        autoHideDuration={6000} 
        onClose={() => setSeedError(null)}
      >
        <Alert 
          severity="error" 
          icon={<AlertCircle size={24} />}
          onClose={() => setSeedError(null)}
        >
          {error || seedError}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default AdminDashboard;
